import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  computed,
  output,
  signal,
} from '@angular/core';
import { MatDrawer, MatDrawerMode } from '@angular/material/sidenav';
import { MatTabGroup } from '@angular/material/tabs';
import { NGXLogger } from 'ngx-logger';
import { Observable, map, of, pairwise, tap } from 'rxjs';

import {
  DXCMatch,
  Field,
  FieldValues,
  Permissions,
  QueuedDocumentMetadata,
} from 'models';
import { DirtyComponent } from 'src/app/models';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { LayoutService } from 'src/app/services/layout.service';
import { ApplicationQuery } from 'src/app/state/application/application.query';
import { ApplicationService } from 'src/app/state/application/application.service';
import { ArchivesQuery } from 'src/app/state/archives/archives.query';

import { toSignal } from '@angular/core/rxjs-interop';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ResizeEvent } from 'angular-resizable-element';
import { assert, assertExists } from 'common';
import { explicitEffect } from 'ngxtension/explicit-effect';
import { TableFieldUIService } from 'src/app/services/table-field-ui.service';
import { UiService } from 'src/app/services/ui.service';
import { IndexerComponent } from '../indexer/indexer.component';
import { UserActionExecutedEvent } from '../user-actions-panel/user-actions-panel.component';

/** Minimum left side width for resizable panel before invoking overlay mode. */
const MIN_LEFT_RESIZE_OVERLAY_PX = 330;
/** Minimum right side width for resizable panel. */
const MIN_RIGHT_RESIZE_PX = 120;
/** Minimum left side width for resizable panel. */
const MIN_LEFT_RESIZE_PX = 64;
/** Index of the indexer tab. */
export const INDEXER_TAB_INDEX = 0;
/** Index of the document history tab. */
export const DOCUMENT_HISTORY_TAB_INDEX = 1;
/** Index of the table field tab. */
export const TABLE_FIELD_TAB_INDEX = 2;

/** Document View Sidebar. */
@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-document-view-sidebar',
  templateUrl: './document-view-sidebar.component.html',
  styleUrls: ['./document-view-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DocumentViewSidebarComponent
  implements DirtyComponent, OnDestroy, OnInit
{
  /**
   * Archive Id.
   */
  @Input()
  archiveId: number;
  /** Document id. */
  @Input()
  documentId: number;
  /** Document secure id. */
  @Input()
  documentSecureId: string;
  /** Draw container reference. */
  @ViewChild('drawer') drawer: MatDrawer;
  /** Field values. */
  @Input()
  fieldValues: FieldValues = [];
  /** Indexer component reference. */
  @ViewChild('indexer')
  indexer: IndexerComponent;
  /**
   * Emits when a DataXChange match is applied to the indexer.
   * The event contains the match that was applied.
   */
  indexerDataXChangeMatchApplied = output<DXCMatch>();
  /** Emits when a field in the indexer is blurred. Contains the field. */
  @Output()
  indexerFieldBlurred = new EventEmitter<Field>();
  /** Emits when a field in the indexer is focused. Contains the field. */
  @Output()
  indexerFieldFocused = new EventEmitter<Field>();
  /** Emits when a field in the indexer is removed. Contains the field that had a value removed.*/
  indexerFieldMultiValueEntryRemoved = output<Field>();
  /** Permissions which can be either archive or search result permissions. */
  @Input()
  permissions: Permissions;
  /** Drawer position. */
  @Input('drawer-position')
  position: 'start' | 'end' = 'end';
  /** Tab controls. */
  @ViewChild('tabs', { static: false }) tabs: MatTabGroup;
  /**
   * Unique id for a document used for the indexer.
   */
  @Input()
  uniqueId: string;
  /** Emits when a user action is executed. */
  @Output()
  userActionExecuted = new EventEmitter<UserActionExecutedEvent>();
  /** Whether viewing an archive revision. */
  @Input()
  viewingArchiveRevision = false;
  /** Viewer page number. */
  @Input()
  viewerPageNumber: number;
  /** Workflow queue document metadata. */
  @Input()
  workflowQueueDocumentMetadata: QueuedDocumentMetadata | undefined = undefined;
  /**
   * Workflow process id.
   *
   * Defaults to 0 meaning there is no process for the document.
   */
  @Input()
  workflowProcessId = 0;

  /** Observable of the active table field. */
  activeTableField$ = this.tableFieldUiService.activeTableField$.pipe(
    untilDestroyed(this)
  );
  /** Observable of whether there is currently an active table field. */
  hasActiveTableField$ = this.activeTableField$.pipe(
    map((activeTableField) => !!activeTableField)
  );
  /** Observable of whether the indexer sidebar should be open. */
  indexerSidebarOpen$ = this.auth.isGuest
    ? of(false) // Never show the sidebar if the user is a guest.
    : this.appQuery.indexerSidebarOpen$;
  /** Is the guest user currently logged in. */
  isGuest = this.auth.isGuest;
  /** Observable state of if we are on a mobile handset sized output. */
  isHandset$ = this.layout.isHandset$.pipe(
    // Disable resize when handset, it just gets complicated.
    tap((isHandset) => (this.isResizeDisabled = isHandset))
  );
  /** Whether the table field tab is currently selected. */
  isTableFieldTabSelected = computed(
    () => this.selectedTabIndex() === TABLE_FIELD_TAB_INDEX
  );
  /** If the ability to resize should be disabled. */
  isResizeDisabled = false;
  /** Whether the user is restricted. */
  isRestrictedUser = this.auth.isRestrictedUser;
  /** A resize event is in progress. */
  resizeInProgress: boolean;
  /** Emits when a resize event is either started or stopped. */
  @Output()
  resizeInProgressChange = new EventEmitter<boolean>();
  /** A CSS object container style adjustments for resize. */
  resizeStyle: { /** Width. */ width?: string } = {
    width: this.appQuery.viewerRightSidebarWidth
      ? `${this.appQuery.viewerRightSidebarWidth}px`
      : undefined,
  };
  /** Currently active tab. */
  selectedTabIndex = signal(INDEXER_TAB_INDEX);
  /** Observable of whether to show the table field tab. */
  shouldShowTableFieldTab$ = this.hasActiveTableField$.pipe(
    map((hasActiveTableField) => {
      return (
        this.appQuery.viewerOpenTableFieldsInSidebar && hasActiveTableField
      );
    })
  );
  private compactMode = toSignal(this.appQuery.useCompactMode$, {
    initialValue: false,
  });
  tableFieldOverrideHeight = computed(() => {
    const compactMode = this.compactMode();
    return compactMode ? 'calc(100vh - 156px)' : 'calc(100vh - 240px)';
  });

  /** Should the drawer overlay mode be forced on. */
  private isDrawerOverlayForced = false;
  /** Last known width of the window. */
  private lastWindowWidth: number;

  /**
   *  Get drawer mode.
   *
   * @returns Observable mat-drawer mode.
   */
  get drawerMode$(): Observable<MatDrawerMode> {
    if (this.isDrawerOverlayForced) {
      return of('over');
    }
    return this.isHandset$.pipe(
      map((isHandset) => (isHandset ? 'over' : 'side'))
    );
  }

  /** @inheritdoc */
  get isDirty(): boolean {
    return typeof this.indexer !== 'undefined' && this.indexer.isDirty;
  }

  /** @inheritdoc */
  get shouldAutoSave(): boolean {
    return (
      this.documentId > 0 &&
      this.archivesQuery.getShouldAutoSave(this.archiveId)
    );
  }

  /**
   * Return is the document history should be shown.
   *
   * @returns boolean.
   */
  get showDocumentHistory(): boolean {
    return (
      !this.isGuest &&
      !this.isRestrictedUser &&
      this.documentId > 0 &&
      this.permissions.viewDocumentHistory
    );
  }

  constructor(
    private logger: NGXLogger,
    private archivesQuery: ArchivesQuery,
    private appQuery: ApplicationQuery,
    private application: ApplicationService,
    private auth: AuthenticationService,
    private tableFieldUiService: TableFieldUIService,
    private layout: LayoutService,
    private ui: UiService,
    private changeDector: ChangeDetectorRef
  ) {
    const shouldShowTableField = toSignal(
      this.shouldShowTableFieldTab$.pipe(untilDestroyed(this))
    );
    explicitEffect(
      [this.tableFieldUiService.focusRequested],
      (focusRequested) => {
        const shouldShow = shouldShowTableField();
        if (focusRequested && shouldShow) {
          this.selectedTabIndex.set(TABLE_FIELD_TAB_INDEX);
        }
      },
      {
        defer: true,
      }
    );
  }

  ngOnDestroy(): void {
    // Unregister component from the UI.
    this.ui.unregister(this);
  }

  ngOnInit(): void {
    // Register as active UI component.
    this.ui.register(this);
    this.shouldShowTableFieldTab$
      .pipe(untilDestroyed(this), pairwise())
      .subscribe(([previouslyActive, currentlyActive]) => {
        if (previouslyActive && !currentlyActive) {
          this.tabs.selectedIndex = INDEXER_TAB_INDEX;
          this.selectedTabIndex.set(INDEXER_TAB_INDEX);
        } else if (!previouslyActive && currentlyActive) {
          this.selectedTabIndex.set(TABLE_FIELD_TAB_INDEX);
        }
      });
  }

  /** Handler for the close drawer event. */
  onClose(): void {
    this.application.setIndexerSidebarOpen(false);
  }

  /**
   * Handler for the indexer's Data XChange match applied event.
   *
   * @param match The DXC match that was applied.
   */
  onIndexerDataXChangeMatchApplied(match: DXCMatch): void {
    this.indexerDataXChangeMatchApplied.emit(match);
  }

  /**
   * Handler for indexer field blurred event.
   *
   * @param field Field that was blurred.
   */
  onIndexerFieldBlurred(field: Field): void {
    this.indexerFieldBlurred.emit(field);
  }

  /**
   * Handler for indexer field focused event.
   *
   * @param field Field that was focused.
   */
  onIndexerFieldFocused(field: Field): void {
    this.indexerFieldFocused.emit(field);
  }

  //TODO resize is not working correctly.

  /**
   * Handler for resize events.
   *
   * @param event Event.
   */
  onResize(event: ResizeEvent): void {
    // Set the style to render the pending size.
    this.resizeStyle = {
      width: `${event.rectangle.width}px`,
    };

    const rectangleWidth = event.rectangle.width;
    assertExists(rectangleWidth);

    // Force the drawer to overlay mode if it would block controls.
    this.isDrawerOverlayForced =
      rectangleWidth >= window.innerWidth - MIN_LEFT_RESIZE_OVERLAY_PX;

    // Update the stored width for layout service.
    this.storeDrawerWidth();
  }

  /**
   * Handler for end of resize event.
   *
   * @param event Event.
   */
  onResizeEnd(event: ResizeEvent): void {
    this.resizeInProgress = false;

    const rectangleWidth = event.rectangle.width;
    assertExists(rectangleWidth);

    // If the resize event ends at minimum, they get a close, because thats an
    // unusable size.
    if (rectangleWidth <= MIN_RIGHT_RESIZE_PX) {
      this.application.setIndexerSidebarOpen(false);
    }

    // Update the known window width.
    this.lastWindowWidth = window.innerWidth;

    // Fix the tabs ink highlight position.
    this.tabs.realignInkBar();
    this.resizeInProgressChange.emit(this.resizeInProgress);
  }

  /**
   * Handler for double click of resize handle.
   *
   * Resets size and state.
   */
  onResizeHandleDoubleClick(): void {
    this.resetPanelSize();
  }

  /**
   * Handler for resize event start.
   */
  onResizeStart(): void {
    // We set this so we can track the state of the resize event.
    // Specically this is required to change our "handle" to cover the
    // entire right panel while we are resizing. This prevents events from
    // falling through to the iFrame of a viewer if active, which breaks events.
    this.resizeInProgress = true;
    this.resizeInProgressChange.emit(this.resizeInProgress);
  }

  /**
   * Handler for resize event validation.
   *
   * @param event Event.
   * @returns If the resize event is valid.
   */
  onResizeValidate(event: ResizeEvent): boolean {
    // Do nothing if resizing is disabled.
    if (this.isResizeDisabled) return false;

    const rectangleWidth = event.rectangle.width;
    assertExists(rectangleWidth);

    // Test the horizontal size of the event rectangle to see if it fits within
    // our resizing bounds.
    const outOfBounds =
      // More than the left side minimum allows.
      rectangleWidth > window.innerWidth - MIN_LEFT_RESIZE_PX ||
      // Less than the right side minimum.
      rectangleWidth < MIN_RIGHT_RESIZE_PX;

    // If it is out of bounds, ignore it.
    return !outOfBounds;
  }

  /**
   * Handler for change of selected tab.
   *
   * @param value Selected tab value, from event.
   */
  onTabChange(value: number): void {
    assert(typeof value == 'number');
    if (value !== this.selectedTabIndex()) {
      this.selectedTabIndex.set(value);
      if (value === TABLE_FIELD_TAB_INDEX) {
        this.resizeStyle.width = this.appQuery.viewerRightSidebarTableFieldWidth
          ? `${this.appQuery.viewerRightSidebarTableFieldWidth}px`
          : undefined;
      } else {
        this.resizeStyle.width = this.appQuery.viewerRightSidebarWidth
          ? `${this.appQuery.viewerRightSidebarWidth}px`
          : undefined;
      }
    }
    // this.logger.debug('Tab changed to index', value);
    // this.storeDrawerWidth();
  }

  /**
   * Handler for the user action executed event.
   *
   * @param event Event.
   */
  onUserActionExecuted(event: UserActionExecutedEvent): void {
    this.userActionExecuted.emit(event);
  }

  /**
   * Handler for resize of the window.
   *
   * Resets size and state, if the width is reduced.
   *
   * @param event Event.
   */
  onWindowResize(event: any): void {
    if (event.target.innerWidth < this.lastWindowWidth) this.resetPanelSize();
    this.lastWindowWidth = event.target.innerWidth;
  }

  /**
   * Switches to the tab with the provided index.
   *
   * @param tabIndex Index for the tab that should be focused. Currently only supports indexer and table field tabs.
   */
  selectTab(tabIndex: number): void {
    assert(
      tabIndex === TABLE_FIELD_TAB_INDEX || tabIndex === INDEXER_TAB_INDEX
    );
    this.selectedTabIndex.set(tabIndex);
  }

  /**
   * Restore size of panel.
   */
  private resetPanelSize() {
    this.resizeStyle = { width: undefined };
    this.isDrawerOverlayForced = false;
    this.application.setViewerRightSidebarWidth(undefined);
    this.application.setViewerRightSidebarTableFieldWidth(undefined);
  }

  /**
   * Update the stored drawer with use by layout.
   */
  private storeDrawerWidth(): void {
    const width = this.drawer._getWidth();
    if (this.appQuery.viewerRightSidebarWidth === width) return;
    this.logger.debug('Storing viewer sidebar width: ', width);
    if (this.selectedTabIndex() === TABLE_FIELD_TAB_INDEX) {
      this.application.setViewerRightSidebarTableFieldWidth(width);
    } else {
      this.application.setViewerRightSidebarWidth(width);
    }
    // Prevent expression change errors when we know we are doing this.
    this.changeDector.detectChanges();
  }
}
